import { Box, Button, Flex, Heading, Icon, Tag, Text, useToast } from "@chakra-ui/react";
import { Tooltip } from "../../../common/components/Tooltip";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCopy, FaEdit, FaEye, FaPlus, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useProcurementTerms } from "../../../common/terminology/useProcurementTerms";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  EventListItemDto,
  ProjectDto,
  PublicEventStatusDto,
  SourcingEventSortField,
  SortDirection,
  useDeleteSourcingEventMutation,
  useListBuyerEventsQuery,
  useUpdateSourcingEventMutation,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useColumnMemory } from "../../../common/entity-table/useColumnMemory";
import { EntityTable } from "../../../common/entity-table/EntityTable";
import { useApiError } from "../../../common/errors/useApiError";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { SelectorValue } from "../../../common/input/Selector/SelectorValue";
import { SingleSelector } from "../../../common/input/Selector/SingleSelector";
import {
  BUCKET_META,
  BucketKey,
  isBucketKey,
  parseStatusBuckets,
  statusesForBuckets,
} from "../../../common/layout/statusBuckets";
import { useNavLabel } from "../../../common/layout/useNavLabel";
import { useRunsPublicProcurements } from "../../../common/organization/useRunsPublicProcurements";
import { Explanation } from "../../../common/support/Explanation/Explanation";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { urls } from "../../../urls";
import { displayPersonName } from "../../../common/persons/displayPersonName";
import { ChooseEventDepartmentModal } from "../../../common/departments/ChooseEventDepartmentModal";
import { useCreateProcurementWithAi } from "../../../common/agent/useCreateProcurementWithAi";
import { useCreateSourcingEvent } from "../../../common/departments/useCreateSourcingEvent";
import { listItemIsDraft } from "../../sourcing-events/eventStatusFilters";
import { useBuyerEventRowActions } from "../../sourcing-events/useBuyerEventRowActions";
import { EventListItemStatusTag } from "../../sourcing-event/EventListItemStatusTag";
import { RemovableFilterChip } from "../../../common/components/RemovableFilterChip";
import { prefersReducedMotion } from "../../../common/scroll";
import { asksToRevealProcurementList } from "./procurementListReveal";

// PUBLISHED is the last V2 status the BFF's requireEditable() allows editing in.
const LINKABLE_EVENT_STATUSES = [PublicEventStatusDto.Published];

export const SourcingEvents = ({ project }: { project: ProjectDto }) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const displayDate = useDisplayDate();
  const displayDateWithTime = useDisplayDateWithTime();
  const authState = useLoggedInWithOrgContextState();
  const runsPublicProcurements = useRunsPublicProcurements();
  const navigate = useNavigate();
  const toast = useToast();
  const apiErrorDisplayer = useApiError();
  const confirmationModal = useConfirmationModal();
  const goToEventEdit = (event: EventListItemDto) => navigate(urls.app.sourcingEvents.view.go(event.id));
  const goToEventView = (event: EventListItemDto) => navigate(urls.app.sourcingEvents.view.go(event.id));
  const navLabel = useNavLabel();
  const [searchParams, setSearchParams] = useSearchParams();
  const statuses = parseStatusBuckets(searchParams.get("status") ?? "");
  const setStatuses = useCallback(
    (buckets: BucketKey[]) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (buckets.length) next.set("status", buckets.join(","));
          else next.delete("status");
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  const [filters, setFilters] = useState<{
    name: string;
    sortBy: SourcingEventSortField | undefined;
    sortDirection: SortDirection | undefined;
    skip: number;
    limit: number;
  }>({
    name: "",
    sortBy: undefined,
    sortDirection: undefined,
    skip: 0,
    limit: 10,
  });

  const queryParams = {
    orgId: authState.selectedOrg.id,
    projectIds: [project.id],
    name: filters.name || undefined,
    statuses: statuses.length ? statusesForBuckets(statuses) : undefined,
    limit: filters.limit,
    skip: filters.skip,
    ...(filters.sortBy && { sortBy: filters.sortBy }),
    ...(filters.sortDirection && { sortDirection: filters.sortDirection }),
  };

  const {
    data: projectEvents,
    error: projectEventsError,
    isLoading: isLoadingProjectEvents,
    isFetching: isFetchingProjectEvents,
  } = useListBuyerEventsQuery(queryParams);

  const location = useLocation();
  const listHeadingRef = useRef<HTMLHeadingElement>(null);
  const revealedForLocation = useRef<string>();
  const isListing = isLoadingProjectEvents || isFetchingProjectEvents;
  useEffect(() => {
    if (!asksToRevealProcurementList(location.state) || isListing) return;
    if (revealedForLocation.current === location.key) return;
    const frame = requestAnimationFrame(() => {
      revealedForLocation.current = location.key;
      listHeadingRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.key, location.state, isListing]);

  const [updateEvent, { isLoading: isUpdatingEvent }] = useUpdateSourcingEventMutation();
  const [deleteSourcingEvent] = useDeleteSourcingEventMutation();
  const createEvent = useCreateSourcingEvent(async (eventId) => {
    try {
      await updateEvent({
        eventId,
        sourcingEventPatchDto: { eventData: { projects: [project.id] } },
      }).unwrap();
    } catch (e) {
      try {
        await deleteSourcingEvent({ eventId }).unwrap();
      } catch (deleteError) {
        apiErrorDisplayer.trigger(deleteError);
      }
      apiErrorDisplayer.trigger(e);
    }
  });
  const createWithAi = useCreateProcurementWithAi(project.id);
  const { copyEvent, copyDisabledReason, copyModal } = useBuyerEventRowActions();

  const [searchName, setSearchName] = useState("");
  const [debouncedSearchName, setDebouncedSearchName] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<EventListItemDto>();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearchName = useCallback(
    debounce((name: string) => setDebouncedSearchName(name), 300),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedTableSearch = useCallback(
    debounce(
      (searchString) =>
        setFilters((prev) => ({
          ...prev,
          name: searchString,
          skip: 0,
        })),
      300
    ),
    []
  );

  const { data: searchedEvents, isFetching: isLoadingSearchedEvents } = useListBuyerEventsQuery({
    orgId: authState.selectedOrg.id,
    statuses: LINKABLE_EVENT_STATUSES,
    name: debouncedSearchName ? debouncedSearchName : undefined,
    limit: 10,
  });

  const onSelectedEvent = async (event: EventListItemDto) => {
    setSelectedEvent(event);
    const updatedProjects = Array.from(new Set([...(event.projects?.map((p) => p.id) ?? []), project.id]));
    try {
      await updateEvent({
        eventId: event.id,
        sourcingEventPatchDto: { eventData: { projects: updatedProjects } },
      }).unwrap();
    } catch (e) {
      apiErrorDisplayer.trigger(e);
    }
    setSearchName("");
    setSelectedEvent(undefined);
  };

  const statusOptions: SelectorValue[] = (Object.keys(BUCKET_META) as BucketKey[]).map((bucket) => ({
    value: bucket,
    label: navLabel(BUCKET_META[bucket].labelKey),
  }));

  const hasActiveFilters = useMemo(() => {
    return filters.name !== "" || statuses.length > 0 || filters.sortBy !== undefined || filters.skip > 0;
  }, [filters, statuses]);

  const defaultColumns = useMemo(() => {
    const base = ["title", "status", "projects", "createdBy", "deadline", "actions"];
    return new Set(runsPublicProcurements ? base : ["title", "internalReference", ...base.slice(1)]);
  }, [runsPublicProcurements]);
  const columnMemory = useColumnMemory("project-procurements");
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
    () => new Set<string>(columnMemory.recall() ?? defaultColumns)
  );
  const computeHidden = (key: string): boolean => !visibleColumns.has(key);
  const chooseColumns = useCallback(
    (next: Set<string>) => {
      columnMemory.remember([...next]);
      setVisibleColumns(next);
    },
    [columnMemory]
  );
  const onShowColumn = useCallback(
    (key: string) => chooseColumns(new Set([...visibleColumns, key])),
    [chooseColumns, visibleColumns]
  );
  const onHideColumn = useCallback(
    (key: string) => chooseColumns(new Set([...visibleColumns].filter((k) => k !== key))),
    [chooseColumns, visibleColumns]
  );
  const hasColumnChanges = useMemo((): boolean => {
    if (visibleColumns.size !== defaultColumns.size) return true;
    for (const k of visibleColumns) if (!defaultColumns.has(k)) return true;
    for (const k of defaultColumns) if (!visibleColumns.has(k)) return true;
    return false;
  }, [visibleColumns, defaultColumns]);

  const resetColumns = useCallback(() => {
    columnMemory.forget();
    setVisibleColumns(new Set(defaultColumns));
  }, [columnMemory, defaultColumns]);

  const handleReset = useCallback(() => {
    setStatuses([]);
    setFilters({
      name: "",
      sortBy: undefined,
      sortDirection: undefined,
      skip: 0,
      limit: 10,
    });
    resetColumns();
  }, [resetColumns, setStatuses]);

  const handleSort = useCallback(
    (column: SourcingEventSortField) => (direction: SortDirection) => {
      setFilters((prev) => ({
        ...prev,
        sortBy: direction ? column : undefined,
        sortDirection: direction,
        skip: 0,
      }));
    },
    []
  );

  if (projectEventsError) return apiErrorDisplayer.show(projectEventsError);

  return (
    <>
      {copyModal}
      {createEvent.isChoosingDepartment && (
        <ChooseEventDepartmentModal
          isOpen
          departments={createEvent.departments}
          isCreating={createEvent.isCreating}
          onCancel={createEvent.cancelChoosingDepartment}
          onConfirm={createEvent.confirmDepartment}
        />
      )}
      {confirmationModal.modal}
      <Box>
        {authState.selectedOrg.canCreateEvent && (
          <Flex columnGap={6} pb="5">
            <Box>
              <Button
                leftIcon={<Icon as={FaPlus} />}
                variant="outline"
                colorScheme="teal"
                size="sm"
                onClick={createWithAi ?? createEvent.start}
              >
                {t("New event")}
              </Button>
            </Box>
            <SingleSelector
              size="sm"
              isClearable
              placeholder={{ text: `${t("Connect sourcing event to this project")}...`, color: "" }}
              isLoading={isLoadingSearchedEvents || isUpdatingEvent}
              value={selectedEvent ? { label: selectedEvent.title, value: selectedEvent.id } : undefined}
              options={searchedEvents?.events.map((event) => ({ value: event.id, label: event.title })) ?? []}
              inputValue={searchName}
              onInputChange={(name: string) => {
                setSearchName(name);
                handleDebouncedSearchName(name);
              }}
              onChange={async (o) => {
                if (o === null) return;
                const event = searchedEvents?.events.find((e) => e.id === o?.value);
                if (!event) throw Error("Event not found");
                await onSelectedEvent(event);
              }}
            />
          </Flex>
        )}
        <Heading ref={listHeadingRef} fontSize="lg" pb="5" sx={{ scrollMarginTop: "4" }}>
          {t("Sourcing events connected to this project")}
        </Heading>
        {statuses.length > 0 && (
          <Flex gap="2" rowGap="3" mb="3" wrap="wrap" data-testid="project-procurement-phase-filters">
            {statuses.map((bucket) => (
              <RemovableFilterChip
                key={bucket}
                label={navLabel(BUCKET_META[bucket].labelKey)}
                onRemove={() => {
                  setStatuses(statuses.filter((other) => other !== bucket));
                  setFilters((prev) => ({ ...prev, skip: 0 }));
                }}
              />
            ))}
          </Flex>
        )}
        <EntityTable
          emptyText={t("No sourcing events yet.")}
          noMatchText={terms.noProcurementsMatchFilters}
          size="sm"
          onShowColumn={onShowColumn}
          onHideColumn={onHideColumn}
          onShowAllColumns={(keys) => chooseColumns(new Set(keys))}
          onResetColumns={resetColumns}
          hasColumnChanges={hasColumnChanges}
          columns={[
            {
              key: "title",
              label: t("Title"),
              onSearch: handleDebouncedTableSearch,
              onSort: handleSort(SourcingEventSortField.Title),
              currentSortDirection: filters.sortBy === SourcingEventSortField.Title ? filters.sortDirection : undefined,
              hidden: computeHidden("title"),
              alwaysShown: true,
            },
            {
              key: "status",
              label: t("Status"),
              hidden: computeHidden("status"),
              onSort: handleSort(SourcingEventSortField.Status),
              currentSortDirection: filters.sortBy === SourcingEventSortField.Status ? filters.sortDirection : undefined,
              multiSelectConfig: {
                options: statusOptions,
                onSelect: (values) => {
                  setStatuses(values.map((v) => v.value).filter(isBucketKey));
                  setFilters((prev) => ({ ...prev, skip: 0 }));
                },
                selectedValues: statuses.map((v) => ({
                  value: v,
                  label: statusOptions.find((s) => s.value === v)?.label || "",
                })),
              },
            },
            {
              key: "projects",
              label: t("Projects"),
              hidden: computeHidden("projects"),
            },
            {
              key: "createdBy",
              label: t("Created by"),
              hidden: computeHidden("createdBy"),
              onSort: handleSort(SourcingEventSortField.CreatedBy),
              currentSortDirection: filters.sortBy === SourcingEventSortField.CreatedBy ? filters.sortDirection : undefined,
            },
            {
              key: "internalReference",
              label: t("Internal reference"),
              onSort: handleSort(SourcingEventSortField.ReferenceNumber),
              currentSortDirection:
                filters.sortBy === SourcingEventSortField.ReferenceNumber ? filters.sortDirection : undefined,
              hidden: computeHidden("internalReference"),
            },
            {
              key: "deadline",
              label: t("Deadline"),
              onSort: handleSort(SourcingEventSortField.Deadline),
              currentSortDirection:
                filters.sortBy === SourcingEventSortField.Deadline ? filters.sortDirection : undefined,
              hidden: computeHidden("deadline"),
            },
            {
              key: "createdAt",
              label: t("Created at"),
              onSort: handleSort(SourcingEventSortField.CreatedAt),
              currentSortDirection:
                filters.sortBy === SourcingEventSortField.CreatedAt ? filters.sortDirection : undefined,
              hidden: computeHidden("createdAt"),
            },
            {
              key: "modifiedAt",
              label: t("Modified at"),
              onSort: handleSort(SourcingEventSortField.ModifiedAt),
              currentSortDirection:
                filters.sortBy === SourcingEventSortField.ModifiedAt ? filters.sortDirection : undefined,
              hidden: computeHidden("modifiedAt"),
            },
            {
              key: "actions",
              label: t("Actions"),
              hidden: computeHidden("actions"),
            },
          ]}
          rows={
            projectEvents?.events?.map((event) => [
              <Button
                key="title"
                variant="unstyled"
                textAlign="left"
                minWidth="60"
                maxWidth="80"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                onClick={() => (listItemIsDraft(event) ? goToEventEdit(event) : goToEventView(event))}
                isDisabled={listItemIsDraft(event) ? !event.canEdit : !event.canView}
              >
                {event.title || t("Untitled")}
              </Button>,
              <Flex key="status" justifyContent="center">
                <EventListItemStatusTag event={event} />
              </Flex>,
              <Flex key="projects" flexDirection="column" maxWidth="full">
                {event.projects?.map((p) => (
                  <Tag key={p.id} size="sm" width="full" maxWidth="40" mb="1">
                    <Tooltip label={p.name} rounded="md" size="sm">
                      <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" width="full">
                        {p.name}
                      </Text>
                    </Tooltip>
                  </Tag>
                ))}
              </Flex>,
              displayPersonName(event.createdBy),
              event.referenceId,
              event.deadline ? displayDateWithTime(event.deadline) : "",
              event.createdAt ? displayDate(event.createdAt) : "",
              event.modifiedAt ? displayDate(event.modifiedAt) : "",
              <Flex key="actions" flexDirection="row" columnGap={1} align="center">
                <Explanation enabled={!event.canEdit} text={copyDisabledReason(event) ?? ""}>
                  <Button
                    isDisabled={!event.canEdit}
                    leftIcon={<Icon as={FaCopy} />}
                    variant="outline"
                    colorScheme="teal"
                    size="xs"
                    onClick={() => copyEvent(event)}
                  >
                    {t("Copy")}
                  </Button>
                </Explanation>
                {listItemIsDraft(event) ? (
                  <>
                    <Explanation text={t("Only admins and creators can edit events")} enabled={!event.canEdit}>
                      <Button
                        leftIcon={<Icon as={FaEdit} w="15px" h="15px" />}
                        variant="outline"
                        colorScheme="teal"
                        isDisabled={!event.canEdit}
                        size="xs"
                        onClick={() => goToEventEdit(event)}
                      >
                        {t("Edit")}
                      </Button>
                    </Explanation>
                    <Explanation text={t("Only admins and creators can delete events")} enabled={!event.canEdit}>
                      <Button
                        leftIcon={<Icon as={FaTimes} w="15px" h="15px" />}
                        variant="outline"
                        isDisabled={!event.canEdit}
                        colorScheme="red"
                        size="xs"
                        onClick={async () => {
                          confirmationModal.show({
                            title: t("Delete draft?"),
                            description: t("Are you sure you want to delete this draft?"),
                            onConfirmed: async () => {
                              try {
                                await deleteSourcingEvent({
                                  eventId: event.id,
                                }).unwrap();
                                toast({
                                  title: t("Successfully deleted!"),
                                  description: t("The draft was successfully deleted"),
                                  status: "success",
                                });
                              } catch (e) {
                                apiErrorDisplayer.trigger(e);
                              }
                            },
                          });
                        }}
                      >
                        {t("Delete")}
                      </Button>
                    </Explanation>
                  </>
                ) : (
                  <Explanation
                    enabled={!event.canView}
                    text={t("Contact your admin to get access to view this event") ?? ""}
                  >
                    <Button
                      leftIcon={<Icon as={FaEye} w="15px" h="15px" />}
                      isDisabled={!event.canView}
                      variant="solid"
                      colorScheme="teal"
                      size="xs"
                      onClick={() => goToEventView(event)}
                    >
                      {t("View event")}
                    </Button>
                  </Explanation>
                )}
              </Flex>,
            ]) ?? []
          }
          isLoading={isLoadingProjectEvents || isFetchingProjectEvents}
          count={projectEvents?.count}
          limit={filters.limit}
          skip={filters.skip}
          setSkip={(skip) => setFilters((prev) => ({ ...prev, skip }))}
          onReset={handleReset}
          hasActiveFilters={hasActiveFilters}
        />
      </Box>
    </>
  );
};
